import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { billingSettingsQueries } from "~/modules/billing/api/billingSettings/billingSettingsQueries.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import UpdateOrderBillingSettingsForm
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/UpdateOrderBillingSettingsSidebar/UpdateOrderBillingSettingsForm.tsx";
import UpdateOrderBillingSettingsQuotaWarning
	from "~/modules/project/components/ProjectDetailsView/components/OrderDetails/components/UpdateOrderBillingSettingsSidebar/UpdateOrderBillingSettingsQuotaWarning.tsx";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	clientId: string;
	order: OrderWithBillingSettings
};

const UpdateOrderBillingSettingsSidebar = ({
	clientId,
	isOpen,
	onClose,
	order,
}: Props) => {

	const queryClient = useQueryClient();
	const handleSuccess = useCallback(async () => {
		await queryClient.refetchQueries({ queryKey: orderQueries.byProject(order.projectId).queryKey });
		onClose();
	}, [onClose, order.projectId, queryClient]);


	const { data: projectBillingSettingsData } = useQuery(billingSettingsQueries.byProject(order.projectId));

	const resolvedOrderBillingSettings = useMemo(() => {
		if (order.billingSettings) {
			return order.billingSettings;
		}

		if (projectBillingSettingsData) {
			return { ...projectBillingSettingsData, billingInstructions: null };
		}
		return undefined;
	}, [order.billingSettings, projectBillingSettingsData]);

	const showUpdateForm = isOpen && !!resolvedOrderBillingSettings && !!projectBillingSettingsData && !order.quotaId;
	const showQuotaWarning = isOpen && !!order.quotaId;

	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={onClose}>
			{!showUpdateForm && !showQuotaWarning && <SidebarBusyOverlay />}
			{showUpdateForm &&
				<UpdateOrderBillingSettingsForm order={order}
												orderBillingSettings={resolvedOrderBillingSettings}
												projectBillingSettings={projectBillingSettingsData}
												clientId={clientId}
												onCancel={onClose}
												onSuccess={handleSuccess} />}
			{showQuotaWarning && <UpdateOrderBillingSettingsQuotaWarning onClose={onClose} />}
		</Sidebar>
	);
};

export default UpdateOrderBillingSettingsSidebar;
